// Shared application models (spec §6, §11). Owned by the integration lead (WP00).
// Changes require lead review; dependents consume inferred types, never redefine them.
import { z } from 'zod';

export const Id = z.string().min(1).max(128).describe('Opaque identifier');
export const IsoTime = z.iso.datetime({ offset: false }).describe('ISO 8601 UTC instant');
const nullable = <T extends z.ZodType>(t: T) => t.nullable();

export const MediaKind = z.enum(['live', 'movie', 'series', 'episode']);
export type MediaKind = z.infer<typeof MediaKind>;
export const CatalogKind = z.enum(['live', 'movie', 'series']);
export type CatalogKind = z.infer<typeof CatalogKind>;

// XC05 capability registry
export const CapabilityState = z.enum(['unknown', 'advertised', 'verified', 'unsupported', 'failed']);
export const CapabilityScope = z.enum(['provider', 'channel', 'asset', 'browser', 'device']);
export const CapabilityFeature = z.enum([
  'player_api', 'xmltv', 'short_epg', 'simple_data_table', 'live', 'vod', 'series',
  'output_hls', 'output_ts', 'catchup', 'start_over', 'range_requests',
  'remux', 'audio_conversion', 'video_transcode', 'multiview',
]);
export const Capability = z.object({
  feature: CapabilityFeature,
  state: CapabilityState,
  scope: CapabilityScope,
  scopeId: nullable(Id),
  evidence: nullable(z.string().max(500)),
  observedAt: IsoTime,
  reason: nullable(z.string().max(500)),
});
export type Capability = z.infer<typeof Capability>;

// AUTH02 / XC02 account metadata
export const AccountStatus = z.enum(['active', 'expired', 'disabled', 'invalid_credentials', 'unknown']);
export const AccountInfo = z.object({
  status: AccountStatus,
  expiresAt: nullable(IsoTime).describe('null = provider did not report expiry (not expired)'),
  maxConnections: nullable(z.number().int().nonnegative()).describe('null = unknown, NOT unlimited'),
  activeConnections: nullable(z.number().int().nonnegative()),
  allowedOutputFormats: z.array(z.string().max(16)).max(16),
  isTrial: nullable(z.boolean()),
  serverTimezone: nullable(z.string().max(64)),
  serverTime: nullable(IsoTime),
  message: nullable(z.string().max(300)).describe('Provider message, plain text'),
});
export type AccountInfo = z.infer<typeof AccountInfo>;

export const CatalogStatus = z.enum(['empty', 'importing', 'ready', 'stale', 'failed']);
export const CatalogSummary = z.object({
  revision: nullable(Id),
  status: CatalogStatus,
  lastSuccessAt: nullable(IsoTime),
  lastAttemptAt: nullable(IsoTime),
  counts: z.object({ live: z.number().int(), movie: z.number().int(), series: z.number().int(), program: z.number().int() }),
  guide: z.object({ status: CatalogStatus, coverageFrom: nullable(IsoTime), coverageTo: nullable(IsoTime), lastSuccessAt: nullable(IsoTime) }),
  error: nullable(z.object({ code: z.string(), message: z.string() })),
});

export const SessionState = z.enum(['connected', 'expired', 'disconnected', 'reauth_required']);
export const Connection = z.object({
  id: Id,
  alias: z.string().max(80),
  serverDisplay: z.string().max(300).describe('scheme://host[:port][/base] with no credentials'),
  insecureTransport: z.boolean().describe('true when the provider link is plain HTTP'),
  adapter: z.enum(['xtream-player-api', 'xtream-extensionless']),
  remembered: z.boolean(),
  sessionState: SessionState,
  sessionExpiresAt: nullable(IsoTime),
  rememberedUntil: nullable(IsoTime),
  account: AccountInfo,
  userConnectionAllowance: nullable(z.number().int().min(1).max(16)).describe('User-confirmed allowance; unknown provider limit defaults to 1'),
  capabilities: z.array(Capability),
  catalog: CatalogSummary,
});
export type Connection = z.infer<typeof Connection>;

export const Category = z.object({
  id: Id,
  kind: CatalogKind,
  name: z.string().max(200),
  order: z.number().int(),
  restricted: z.boolean().describe('Adult/age-restricted per provider flags or name rules (SAFE01)'),
});
export type Category = z.infer<typeof Category>;

export const Provenance = z.enum(['provider_reported', 'verified']);
export const QualityLabel = z.object({ value: z.string().max(40), provenance: Provenance });

export const Channel = z.object({
  id: Id,
  kind: z.literal('live'),
  name: z.string().max(200),
  number: nullable(z.number().int()),
  categoryIds: z.array(Id),
  logo: nullable(Id).describe('artwork id → GET /api/artwork/{id}'),
  epgChannelId: nullable(z.string().max(200)),
  archive: z.object({ advertised: z.boolean(), days: nullable(z.number().int().nonnegative()) }),
  sourceId: Id.describe('Playable source reference for POST /api/playback'),
  available: z.boolean(),
});
export type Channel = z.infer<typeof Channel>;

export const MovieSummary = z.object({
  id: Id,
  kind: z.literal('movie'),
  title: z.string().max(300),
  year: nullable(z.number().int()),
  categoryIds: z.array(Id),
  poster: nullable(Id),
  rating: nullable(z.string().max(20)),
  addedAt: nullable(IsoTime),
  durationSec: nullable(z.number().int().nonnegative()),
  quality: nullable(QualityLabel),
  available: z.boolean(),
});
export type MovieSummary = z.infer<typeof MovieSummary>;

export const SeriesSummary = z.object({
  id: Id,
  kind: z.literal('series'),
  title: z.string().max(300),
  year: nullable(z.number().int()),
  categoryIds: z.array(Id),
  poster: nullable(Id),
  rating: nullable(z.string().max(20)),
  updatedAt: nullable(IsoTime),
  available: z.boolean(),
});
export type SeriesSummary = z.infer<typeof SeriesSummary>;

export const CatalogItem = z.discriminatedUnion('kind', [Channel, MovieSummary, SeriesSummary]);
export type CatalogItem = z.infer<typeof CatalogItem>;

export const PlayableSource = z.object({
  id: Id,
  label: z.string().max(120),
  containerExtension: nullable(z.string().max(10)),
  quality: nullable(QualityLabel),
  edition: nullable(z.string().max(80)),
  playability: CapabilityState.describe('verified only after a real playback proof'),
  audioLanguages: z.array(z.string().max(16)),
  subtitleLanguages: z.array(z.string().max(16)),
});
export type PlayableSource = z.infer<typeof PlayableSource>;

export const Progress = z.object({
  itemId: Id,
  positionSec: z.number().nonnegative(),
  durationSec: nullable(z.number().nonnegative()),
  completed: z.boolean(),
  manual: nullable(z.boolean()).describe('Manual watched override; null = automatic'),
  sessionId: nullable(Id),
  seq: z.number().int().nonnegative(),
  updatedAt: IsoTime,
});
export type Progress = z.infer<typeof Progress>;

export const MovieDetail = MovieSummary.extend({
  plot: nullable(z.string().max(4000)),
  genres: z.array(z.string().max(60)),
  cast: z.array(z.string().max(120)),
  director: nullable(z.string().max(200)),
  releaseDate: nullable(z.string().max(32)),
  backdrop: nullable(Id),
  trailerAvailable: z.boolean(),
  sources: z.array(PlayableSource),
  progress: nullable(Progress),
});
export type MovieDetail = z.infer<typeof MovieDetail>;

export const Episode = z.object({
  id: Id,
  seriesId: Id,
  season: z.number().int().nonnegative().describe('0 = specials'),
  number: nullable(z.number().int()),
  title: z.string().max(300),
  plot: nullable(z.string().max(4000)),
  durationSec: nullable(z.number().int().nonnegative()),
  airDate: nullable(z.string().max(32)),
  still: nullable(Id),
  source: PlayableSource,
  duplicateNumber: z.boolean(),
  available: z.boolean(),
  progress: nullable(Progress),
});
export type Episode = z.infer<typeof Episode>;

export const SeasonGap = z.object({
  missingNumbers: z.array(z.number().int()),
  confirmed: z.boolean().describe('true only with a known complete inventory; otherwise "Possible gap"'),
});
export const Season = z.object({
  number: z.number().int().nonnegative(),
  name: z.string().max(120),
  isSpecials: z.boolean(),
  episodes: z.array(Episode),
  gaps: z.array(SeasonGap),
});
export const SeriesDetail = SeriesSummary.extend({
  plot: nullable(z.string().max(4000)),
  genres: z.array(z.string().max(60)),
  cast: z.array(z.string().max(120)),
  backdrop: nullable(Id),
  seasons: z.array(Season),
  inventory: z.enum(['complete', 'unknown']),
  nextEpisodeId: nullable(Id),
});
export type SeriesDetail = z.infer<typeof SeriesDetail>;

export const ArchiveState = z.enum(['available', 'unavailable', 'unknown']);
export const Program = z.object({
  id: Id,
  channelId: Id,
  start: IsoTime,
  end: IsoTime,
  title: z.string().max(300),
  description: nullable(z.string().max(4000)),
  episodeLabel: nullable(z.string().max(60)),
  archive: ArchiveState,
  provenance: z.enum(['xmltv', 'short_epg', 'simple_data_table']),
});
export type Program = z.infer<typeof Program>;

export const Freshness = z.object({ fetchedAt: nullable(IsoTime), staleAt: nullable(IsoTime), stale: z.boolean() });

// User state (STORE03)
export const UserListName = z.enum(['favorite', 'watchlist', 'follow', 'hidden']);
export const UserListEntry = z.object({ list: UserListName, itemId: Id, kind: MediaKind, addedAt: IsoTime, available: z.boolean() });
export const ReminderState = z.enum(['scheduled', 'moved', 'cancelled', 'fired']);
export const Reminder = z.object({
  id: Id,
  programId: Id,
  channelId: Id,
  title: z.string().max(300),
  start: IsoTime,
  end: IsoTime,
  state: ReminderState,
});
export type Reminder = z.infer<typeof Reminder>;
export const Preferences = z.object({
  startPage: z.enum(['home', 'live', 'movies', 'series', 'mytv']),
  timezone: nullable(z.string().max(64)).describe('null = browser zone'),
  guideDisplayOffsetMinutes: z.number().int().min(-720).max(720).describe('Reversible display correction; never applied to catch-up requests'),
  pinnedChannelIds: z.array(Id).max(50),
  channelOrder: z.array(Id).max(20000),
  autoplayNextEpisode: z.boolean(),
  spoilerProtection: z.boolean(),
  audioLanguage: nullable(z.string().max(16)),
  subtitleLanguage: nullable(z.string().max(16)),
  multiviewLayout: z.enum(['single', 'side_by_side', 'main_plus_two', 'grid_2x2']),
  hiddenCategoryIds: z.array(Id).max(2000),
  searchExcludedCategoryIds: z.array(Id).max(2000),
  sharedDeviceMode: z.boolean(),
});
export type Preferences = z.infer<typeof Preferences>;

export const Profile = z.object({
  id: Id,
  name: z.string().max(60),
  pinSet: z.boolean(),
  restrictedUnlocked: z.boolean().describe('PIN gate currently unlocked for this device session'),
  childMode: z.boolean(),
});

export const UserState = z.object({
  revision: z.number().int().nonnegative(),
  lists: z.array(UserListEntry),
  progress: z.array(Progress),
  reminders: z.array(Reminder),
  preferences: Preferences,
});
export type UserState = z.infer<typeof UserState>;

export const ClientCapabilities = z.object({
  nativeHls: z.boolean(),
  mse: z.boolean(),
  codecs: z.record(z.string().max(32), z.boolean()).describe('e.g. {"avc1.64001f":true,"mp4a.40.2":true,"ac-3":false}'),
  mobile: z.boolean(),
  pictureInPicture: z.boolean(),
  convert: z.object({
    audio: z.array(z.string().max(16)).max(16).describe('Audio codecs the client can convert in-browser, e.g. ["ac3","eac3"] (ADR-0004)'),
    containers: z.array(z.string().max(16)).max(8).describe('Containers the client can remux in-browser, e.g. ["mkv","mov"]'),
  }).default({ audio: [], containers: [] }),
});
export type ClientCapabilities = z.infer<typeof ClientCapabilities>;
